import React, { useState } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { cn } from "@/lib/utils";
import { ChevronDown, LogOut, UserCog } from "lucide-react";
import { navGroups } from "./navConfig";
import MurphyLogo from "@/components/shared/MurphyLogo";
import { useAuth } from "@/lib/AuthContext";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner"; // eslint-disable-line no-unused-vars

function NavItem({ item, currentPageName, onNavigate }) {
  const childActive = (item.children || []).some((c) => c.page === currentPageName);
  const isActive = item.page === currentPageName;
  const [open, setOpen] = useState(childActive);
  const Icon = item.icon;

  return (
    <div>
      <div className="group relative flex items-center">
        <Link
          to={createPageUrl(item.page)}
          onClick={onNavigate}
          className={cn(
            "relative flex-1 flex items-center gap-3 pl-4 pr-2 py-2.5 rounded-xl text-[13.5px] transition-all duration-200",
            isActive
              ? "bg-brand-limeSoft text-brand-ink font-semibold"
              : "text-slate-500 font-medium hover:text-brand-ink hover:bg-slate-100/80"
          )}
        >
          {isActive && (
            <span className="absolute left-0 top-2 bottom-2 w-1 rounded-full bg-brand-limeDark" />
          )}
          <Icon
            className={cn(
              "w-[18px] h-[18px] shrink-0 transition-colors",
              isActive ? "text-brand-ink" : "text-slate-400 group-hover:text-brand-ink"
            )}
          />
          <span className="truncate">{item.name}</span>
        </Link>
        {item.children && (
          <button
            onClick={() => setOpen((v) => !v)}
            aria-label="Vis undermeny"
            className="absolute right-1.5 p-1.5 rounded-lg text-slate-400 hover:text-brand-ink hover:bg-slate-100 transition-colors"
          >
            <ChevronDown className={cn("w-4 h-4 transition-transform duration-200", (open || childActive) && "rotate-180")} />
          </button>
        )}
      </div>
      {item.children && (open || childActive) && (
        <div className="mt-1 mb-1 ml-6 pl-3 border-l border-slate-200 space-y-0.5">
          {item.children.map((child) => {
            const ChildIcon = child.icon;
            const active = child.page === currentPageName;
            return (
              <Link
                key={child.page}
                to={createPageUrl(child.page)}
                onClick={onNavigate}
                className={cn(
                  "flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] transition-all",
                  active
                    ? "text-brand-ink font-semibold bg-brand-limeSoft/60"
                    : "text-slate-500 font-medium hover:text-brand-ink hover:bg-slate-100/80"
                )}
              >
                <ChildIcon className={cn("w-4 h-4", active ? "text-brand-limeDark" : "text-slate-400")} />
                {child.name}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function UserFooter() {
  const { user, logout } = useAuth();
  const name = user?.full_name || "Bruker";
  const initials = name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const roleLabel = user?.role === "admin" ? "Administrator" : user?.role === "external" ? "Ekstern part" : "Medarbeider";

  return (
    <div className="p-3 border-t border-slate-100">
      <DropdownMenu>
        <DropdownMenuTrigger className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-slate-100 transition-colors text-left outline-none">
          <span className="w-9 h-9 rounded-full bg-brand-ink text-brand-lime flex items-center justify-center text-xs font-bold shrink-0">
            {initials}
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-semibold text-brand-ink truncate">{name}</span>
            <span className="block text-xs text-slate-400 truncate">{roleLabel}</span>
          </span>
          <ChevronDown className="w-4 h-4 text-slate-400" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 mb-2">
          <DropdownMenuLabel>
            <div className="font-semibold text-brand-ink">{name}</div>
            <div className="text-xs font-normal text-slate-400">{user?.email}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to={createPageUrl("UserSettings")} className="cursor-pointer">
              <UserCog className="w-4 h-4 mr-2" /> Innstillinger
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="cursor-pointer text-red-600 focus:text-red-600"
            onClick={logout}
          >
            <LogOut className="w-4 h-4 mr-2" /> Logg ut
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function SidebarContent({ currentPageName, onNavigate }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isExternal = user?.role === "external";
  return (
    <div className="flex flex-col h-full bg-white">
      <Link
        to={createPageUrl(isExternal ? "Deals" : "Dashboard")}
        onClick={onNavigate}
        className="flex items-center h-16 px-6 border-b border-slate-100 shrink-0 hover:opacity-80 transition-opacity duration-200"
        aria-label="Gå til start"
      >
        <MurphyLogo size="md" />
      </Link>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {navGroups.map((group, gi) => {
          const items = group.items.filter((it) => (!it.adminOnly || isAdmin) && (!isExternal || it.externalOk));
          if (items.length === 0) return null;
          return (
            <div key={gi} className="space-y-1">
              {group.label && (
                <p className="px-4 pb-1 text-[10.5px] font-semibold uppercase tracking-[0.13em] text-slate-400">
                  {group.label}
                </p>
              )}
              {items.map((item) => (
                <NavItem key={item.page} item={item} currentPageName={currentPageName} onNavigate={onNavigate} />
              ))}
            </div>
          );
        })}
      </nav>
      <UserFooter />
    </div>
  );
}

export default function Sidebar({ currentPageName, mobileOpen, setMobileOpen }) {
  return (
    <>
      <aside className="hidden lg:flex lg:fixed lg:inset-y-0 lg:left-0 lg:w-72 lg:flex-col border-r border-slate-200 z-30">
        <SidebarContent currentPageName={currentPageName} />
      </aside>
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-72 max-w-[85vw]">
          <SidebarContent currentPageName={currentPageName} onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>
    </>
  );
}
